/* Reviews section aligned with the Onze werelden styling: cream canvas, dark-green typography, white rounded cards, and compact spacing. */

import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { motion } from 'framer-motion';

interface Review {
  id: string;
  name: string;
  text: string;
  stars: number;
  created_at: string;
}

export default function Reviews() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(1);

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth >= 1280) {
        setVisibleCount(3);
      } else if (window.innerWidth >= 1024) {
        setVisibleCount(2);
      } else {
        setVisibleCount(1);
      }
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  async function fetchReviews() {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('is_published', true)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setIsLoading(false);
    }
  }

  const getVisibleReviews = () => {
    if (reviews.length === 0) return [];

    const result = [];
    for (let i = 0; i < visibleCount; i++) {
      const index = (currentIndex + i) % reviews.length;
      result.push(reviews[index]);
    }

    return result;
  };

  const nextReview = () => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const renderStars = (count: number) => {
    return (
      <div className="flex gap-1 justify-center">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-5 h-5 ${i < count ? 'fill-amber-400 text-amber-400' : 'fill-[#e7e1d6] text-[#e7e1d6]'}`}
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <section className="bg-[#fcfaf6] py-20 text-[#1e2b22] sm:py-24 lg:py-28">
        <div className="mx-auto max-w-[1240px] px-5 lg:px-8">
          <div className="text-center text-[#5a6552]">Laden...</div>
        </div>
      </section>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  const visibleReviews = getVisibleReviews();

  return (
    <section data-fast-scroll="viewed_reviews" className="relative isolate overflow-hidden bg-[#fcfaf6] py-20 text-[#1e2b22] sm:py-24 lg:py-28">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(100%_60%_at_50%_0%,rgba(137,146,127,0.08),transparent_60%)]"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-[1240px] px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="mb-10 max-w-2xl sm:mb-12"
        >
          <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">
            Wat klanten zeggen
          </p>
          <h2 className="text-4xl tracking-[-0.035em] text-[#1e2b22] md:text-5xl">
            Reviews
          </h2>
          <p className="mt-3 max-w-xl text-base leading-7 text-[#5a6552] sm:text-lg sm:leading-8">
            Ervaringen van klanten die met ons aan de slag gingen in hun buitenruimte.
          </p>
        </motion.div>

        <div className="relative">
          <div className="flex items-center gap-4">
            {reviews.length > 1 && (
              <button
                onClick={prevReview}
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white px-3 text-[#1e2b22] ring-1 ring-[#e7e1d6] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#e7e1d6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5a6552]"
                aria-label="Vorige review"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}

            <div className="flex-1 grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
              {visibleReviews.map((review, index) => (
                <motion.article
                  key={review.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  viewport={{ once: true }}
                  className="relative flex min-h-[260px] flex-col rounded-xl bg-white p-8 pt-10 text-[#1e2b22] ring-1 ring-[#e7e1d6] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_14px_30px_rgba(30,43,34,0.12)]"
                >
                  <div className="mb-6">
                    {renderStars(review.stars)}
                  </div>

                  <p className="flex-1 text-center text-base leading-7 text-[#5a6552]">
                    {review.text}
                  </p>

                  <p className="mt-8 text-center text-lg font-semibold text-[#1e2b22]">
                    {review.name}
                  </p>
                </motion.article>
              ))}
            </div>

            {reviews.length > 1 && (
              <button
                onClick={nextReview}
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white px-3 text-[#1e2b22] ring-1 ring-[#e7e1d6] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#e7e1d6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5a6552]"
                aria-label="Volgende review"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
